import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Button,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import Actions from '../constants/Actions';
import { removeFromCart, addItemQty, removeItemQty } from '../actions/index';
import Iconify from '../components/Iconify';
import './Cart.css';
import CartItemResumo from './CartItemResumo';
import ModalAutorizacaoDesconto from './ModalAutorizacaoDesconto';
import { getValorSubTotal, validaTotalGeral } from '../utils/functions';

const money = (value) =>
    `R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`;

const RowLine = ({ label, value, muted, emphasis, valueColor }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography
            sx={{
                fontSize: emphasis ? '0.95rem' : '0.8rem',
                color: muted ? 'text.secondary' : 'text.primary',
                fontWeight: emphasis ? 700 : 500,
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: emphasis ? '1.15rem' : '0.85rem',
                fontWeight: emphasis ? 800 : 600,
                color: valueColor || (emphasis ? 'text.primary' : 'text.primary'),
                whiteSpace: 'nowrap',
            }}
        >
            {value}
        </Typography>
    </Stack>
);

const CartResumo = () => {
    const cartItems = useSelector((state) => state.cartItems);
    const valorFrete = useSelector((state) => state.valorFrete);
    const valorDesconto = useSelector((state) => state.valorDesconto);
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);

    const removerProdutoCarrinho = (product) => {
        dispatch(removeFromCart(product));
    };

    const adicionarQtdItemCarrinho = (product) => {
        dispatch(addItemQty(product));
    };

    const removerQtdItemCarrinho = (product) => {
        dispatch(removeItemQty(product));
    };

    const subtotal = getValorSubTotal(cartItems);
    const total = validaTotalGeral(cartItems, valorFrete, valorDesconto);
    const descontoPedido = parseFloat(valorDesconto) || 0;
    const qtdItens = cartItems.length;

    useEffect(() => {
        if (descontoPedido <= 0) return;
        if (cartItems.length === 0 || descontoPedido > subtotal) {
            dispatch({ type: Actions.CLEAR_VALOR_DESCONTO });
        }
    }, [cartItems.length, descontoPedido, subtotal, dispatch]);

    const handleAutorizadoPedido = ({ valorReais, autorizacao, modo, valorInformado }) => {
        dispatch({
            type: Actions.SET_VALOR_DESCONTO,
            valorDesconto: Number(Number(valorReais).toFixed(2)),
            descontoAutorizacao: {
                ...autorizacao,
                valorReais: Number(Number(valorReais).toFixed(2)),
                modo,
                valorInformado,
                escopo: 'pedido',
            },
        });
    };

    const handleRemoverDescontoPedido = () => {
        dispatch({ type: Actions.CLEAR_VALOR_DESCONTO });
    };

    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.25 }}
            >
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                    {qtdItens === 0
                        ? 'Nenhum item'
                        : `${qtdItens} ${qtdItens === 1 ? 'item' : 'itens'}`}
                </Typography>
            </Stack>

            <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: 0.25 }}>
                {qtdItens > 0 ? (
                    <Stack spacing={1}>
                        {cartItems.map((cartItem, index) => (
                            <CartItemResumo
                                key={`${cartItem.product_id}-${cartItem.grade}-${cartItem.filial}-${index}`}
                                cartItem={cartItem}
                                removeFromCart={removerProdutoCarrinho}
                                addItemQty={adicionarQtdItemCarrinho}
                                removeItemQty={removerQtdItemCarrinho}
                            />
                        ))}
                    </Stack>
                ) : (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                        <Iconify icon="mdi:cart-remove" color="#bbb" height="44" />
                        <Typography sx={{ mt: 1.25, color: 'text.disabled', fontSize: '0.82rem' }}>
                            Sem produtos no carrinho
                        </Typography>
                    </Stack>
                )}
            </Box>

            <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1.5 }} />

                <Stack spacing={0.85}>
                    <RowLine label="Subtotal" value={money(subtotal)} muted />
                    {descontoPedido > 0 && (
                        <RowLine
                            label="Desconto"
                            value={`− ${money(descontoPedido)}`}
                            valueColor="success.main"
                        />
                    )}
                    <RowLine
                        label="Frete"
                        value={valorFrete > 0 ? money(valorFrete) : 'Grátis'}
                        valueColor={valorFrete > 0 ? undefined : 'success.main'}
                        muted
                    />
                </Stack>

                <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                    <Button
                        fullWidth
                        size="small"
                        variant={descontoPedido > 0 ? 'contained' : 'outlined'}
                        color={descontoPedido > 0 ? 'success' : 'primary'}
                        disabled={qtdItens === 0 || subtotal <= 0}
                        onClick={() => setModalOpen(true)}
                        startIcon={<Iconify width={16} icon="mdi:tag-outline" />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 1.25,
                            py: 0.75,
                        }}
                    >
                        {descontoPedido > 0 ? 'Alterar desconto do pedido' : 'Desconto no pedido'}
                    </Button>
                    {descontoPedido > 0 && (
                        <Button
                            fullWidth
                            size="small"
                            color="inherit"
                            onClick={handleRemoverDescontoPedido}
                            sx={{
                                textTransform: 'none',
                                color: 'text.secondary',
                                fontSize: '0.78rem',
                            }}
                        >
                            Remover desconto do pedido
                        </Button>
                    )}
                </Stack>

                <Box
                    sx={{
                        mt: 1.75,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 1.5,
                        bgcolor: '#0f172a',
                        color: '#fff',
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
                            Total
                        </Typography>
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, whiteSpace: 'nowrap' }}>
                            {money(total)}
                        </Typography>
                    </Stack>
                </Box>
            </Box>

            <ModalAutorizacaoDesconto
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                escopo="pedido"
                baseValor={subtotal}
                titulo="Desconto no pedido"
                onAutorizado={handleAutorizadoPedido}
            />
        </Box>
    );
};

export default CartResumo;
